/**
 * Déroulement d'une recherche — EF-POI-009, EF-POI-012, EF-POI-014,
 * EF-MOD-008, EF-RBK-013.
 *
 * L'état de recherche est explicitement sérialisable : une destruction inopinée
 * du contexte web ne doit perdre ni les tronçons obtenus, ni le choix de
 * EF-POI-012 restant à faire (CA-038).
 */

import type { CategoryKey } from '../core/categories';
import type { LocalDateTime } from '../core/local-datetime';
import type { CoveredRange, Poi, PoiId, ProgressionInterval } from '../core/model';
import type { Coordinate } from '../geo/geodesy';
import type { PassageComputation } from '../geo/passages';
import {
  DEFAULT_RETRY_AFTER_SECONDS,
  MAX_AUTOMATIC_RETRIES,
  MAX_TEMPORARY_WAITS,
  OverpassClient,
  type ChunkResult,
} from './client';
import {
  countAutomaticPois,
  finalizePresence,
  mergeInterpretedObjects,
  RESULT_CAP,
  RESULT_WARNING,
} from './merge';
import { buildQuery, requestRadiusMeters, type SearchChunk } from './query';
import { interpretObject, type InterpretedObject } from './response';

export type SearchStopReason = 'complete' | 'budget' | 'cap' | 'cancelled' | 'suspended';

/** État persistable d'une recherche, complet ou interrompu. */
export interface SearchState {
  readonly parameterSetId: string;
  readonly chunkCount: number;
  /** Rangs des tronçons restant à émettre, dans l'ordre. */
  readonly pendingChunkIndices: readonly number[];
  readonly coveredRanges: readonly CoveredRange[];
  readonly missingRanges: readonly ProgressionInterval[];
  readonly missingChunkCount: number;
  readonly consumedBudgetSeconds: number;
  readonly osmObjectsReceived: number;
  readonly poisRejectedByFilters: number;
  readonly temporaryWaitsUsed: number;
  readonly seenKeys: readonly string[];
  readonly rejectedCauses: readonly (readonly [string, string])[];
}

export interface SearchProgress {
  readonly completedChunks: number;
  readonly totalChunks: number;
  readonly consumedBudgetSeconds: number;
  readonly remainingBudgetSeconds: number;
  readonly automaticPoiCount: number;
  /** Message décrivant l'attente en cours, le cas échéant. */
  readonly waitingMessage: string | null;
}

export interface SearchRequest {
  readonly chunks: readonly SearchChunk[];
  readonly categories: readonly CategoryKey[];
  readonly corridorMeters: number;
  readonly budgetSeconds: number;
  readonly endpointUrl: string;
  readonly parameterSetId: string;
  readonly searchedAtLocal: LocalDateTime;
  readonly searchedFromMeters: number;
  readonly searchedToMeters: number;
  readonly computePassages: (coordinate: Coordinate, reachMeters: number) => readonly PassageComputation[];
  /** Nombre de POI automatiques hérités des recherches précédentes. */
  readonly inheritedAutomaticCount: number;
}

export interface SearchOutcome {
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly state: SearchState;
  readonly stoppedBy: SearchStopReason;
  readonly addedCount: number;
  readonly mergedCount: number;
  /** Vrai lorsque le seuil d'avertissement de 7 000 POI a été franchi. */
  readonly warningReached: boolean;
  readonly lastFailureCause: string | null;
}

export function initialSearchState(request: SearchRequest): SearchState {
  return {
    parameterSetId: request.parameterSetId,
    chunkCount: request.chunks.length,
    pendingChunkIndices: request.chunks.map((chunk) => chunk.index),
    coveredRanges: [],
    missingRanges: [],
    missingChunkCount: 0,
    consumedBudgetSeconds: 0,
    osmObjectsReceived: 0,
    poisRejectedByFilters: 0,
    temporaryWaitsUsed: 0,
    seenKeys: [],
    rejectedCauses: [],
  };
}

export interface RunSearchOptions {
  readonly client: OverpassClient;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: SearchProgress) => void;
  /** Vrai lorsque l'application est passée à l'arrière-plan (EF-MOD-008). */
  readonly shouldSuspend?: () => boolean;
  readonly now?: () => number;
  readonly sleep?: (milliseconds: number) => Promise<void>;
}

const defaultSleep = (milliseconds: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

/**
 * Émet les tronçons restants, un par un. Le budget est décompté à partir du
 * premier envoi, temps d'attente entre essais compris ; son épuisement arrête
 * la recherche et applique EF-POI-012.
 */
export async function runSearch(
  request: SearchRequest,
  startingPois: ReadonlyMap<PoiId, Poi>,
  state: SearchState,
  options: RunSearchOptions,
): Promise<SearchOutcome> {
  const now = options.now ?? ((): number => Date.now());
  const sleep = options.sleep ?? defaultSleep;
  const radiusMeters = requestRadiusMeters(request.corridorMeters);
  const enabled = new Set(request.categories);
  const chunkByIndex = new Map(request.chunks.map((chunk) => [chunk.index, chunk]));

  let pois = startingPois;
  const pending = [...state.pendingChunkIndices];
  const coveredRanges = [...state.coveredRanges];
  const missingRanges = [...state.missingRanges];
  const seenKeys = new Set(state.seenKeys);
  const rejectedCauses = new Map(state.rejectedCauses);

  let missingChunkCount = state.missingChunkCount;
  let consumedBudgetSeconds = state.consumedBudgetSeconds;
  let osmObjectsReceived = state.osmObjectsReceived;
  let poisRejectedByFilters = state.poisRejectedByFilters;
  let temporaryWaitsUsed = state.temporaryWaitsUsed;
  let addedCount = 0;
  let mergedCount = 0;
  let warningReached = countAutomaticPois(pois) >= RESULT_WARNING;
  let lastFailureCause: string | null = null;
  let stoppedBy: SearchStopReason = 'complete';

  const startedAt = now();
  const elapsed = (): number => consumedBudgetSeconds + (now() - startedAt) / 1000;

  const report = (waitingMessage: string | null): void => {
    options.onProgress?.({
      completedChunks: state.chunkCount - pending.length,
      totalChunks: state.chunkCount,
      consumedBudgetSeconds: elapsed(),
      remainingBudgetSeconds: Math.max(0, request.budgetSeconds - elapsed()),
      automaticPoiCount: countAutomaticPois(pois),
      waitingMessage,
    });
  };

  report(null);

  while (pending.length > 0) {
    if (options.signal?.aborted === true) {
      stoppedBy = 'cancelled';
      break;
    }
    if (options.shouldSuspend?.() === true) {
      stoppedBy = 'suspended';
      break;
    }
    if (elapsed() >= request.budgetSeconds) {
      stoppedBy = 'budget';
      break;
    }

    const chunkIndex = pending[0];
    if (chunkIndex === undefined) break;
    const chunk = chunkByIndex.get(chunkIndex);
    if (chunk === undefined) {
      pending.shift();
      continue;
    }

    const query = buildQuery(request.categories, chunk.points, radiusMeters);
    let result: ChunkResult | null = null;
    let attempts = 0;

    // Un essai initial, plus au plus un essai automatique par tronçon.
    while (attempts <= MAX_AUTOMATIC_RETRIES) {
      attempts += 1;
      result = await options.client.request(request.endpointUrl, query, options.signal);
      if (result.kind === 'ok') break;

      if (result.failure === 'temporary') {
        if (temporaryWaitsUsed >= MAX_TEMPORARY_WAITS) break;
        temporaryWaitsUsed += 1;
        const waitSeconds = result.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS;
        report(`Service temporairement indisponible : nouvelle tentative dans ${waitSeconds} s.`);
        await sleep(waitSeconds * 1000);
        // L'attente ne consomme pas d'essai automatique supplémentaire.
        attempts -= 1;
        if (elapsed() >= request.budgetSeconds) break;
        continue;
      }

      if (result.failure === 'client' || result.failure === 'truncated') break;
      if (attempts > MAX_AUTOMATIC_RETRIES) break;
    }

    pending.shift();

    if (result === null || result.kind === 'failed') {
      lastFailureCause = result?.cause ?? 'Cause inconnue.';
      missingChunkCount += 1;
      missingRanges.push({
        fromProgressionMeters: chunk.fromProgressionMeters,
        toProgressionMeters: chunk.toProgressionMeters,
      });
      report(null);
      continue;
    }

    osmObjectsReceived += result.response.objects.length;

    const interpreted: InterpretedObject[] = [];
    for (const object of result.response.objects) {
      const item = interpretObject(object, enabled);
      if (item !== null) interpreted.push(item);
    }

    const merge = mergeInterpretedObjects(pois, interpreted, {
      corridorMeters: request.corridorMeters,
      computePassages: request.computePassages,
    });
    pois = merge.pois;
    addedCount += merge.addedCount;
    mergedCount += merge.mergedCount;
    poisRejectedByFilters += merge.rejectedCount;
    for (const key of merge.seenKeys) seenKeys.add(key);
    for (const [key, cause] of merge.rejectedCauses) rejectedCauses.set(key, cause);

    coveredRanges.push({
      fromProgressionMeters: chunk.fromProgressionMeters,
      toProgressionMeters: chunk.toProgressionMeters,
      parameterSetId: request.parameterSetId,
      searchedAtLocal: request.searchedAtLocal,
    });

    const automaticCount = countAutomaticPois(pois);
    if (automaticCount >= RESULT_WARNING) warningReached = true;
    report(null);

    // Le plafond est évalué en continu après le traitement de chaque tronçon.
    if (automaticCount >= RESULT_CAP) {
      stoppedBy = 'cap';
      break;
    }
  }

  if (stoppedBy === 'complete' && pending.length > 0) stoppedBy = 'budget';

  const finalState: SearchState = {
    parameterSetId: request.parameterSetId,
    chunkCount: state.chunkCount,
    pendingChunkIndices: pending,
    coveredRanges: mergeIntervals(coveredRanges),
    missingRanges: subtractIntervals(mergeIntervals(missingRanges), mergeIntervals(coveredRanges)),
    missingChunkCount,
    consumedBudgetSeconds: elapsed(),
    osmObjectsReceived,
    poisRejectedByFilters,
    temporaryWaitsUsed,
    seenKeys: [...seenKeys],
    rejectedCauses: [...rejectedCauses],
  };

  const finalPois = finalizePresence(pois, {
    seenKeys,
    rejectedCauses,
    coveredRanges: finalState.coveredRanges,
    searchedFromMeters: request.searchedFromMeters,
    searchedToMeters: request.searchedToMeters,
  });

  return {
    pois: finalPois,
    state: finalState,
    stoppedBy,
    addedCount,
    mergedCount,
    warningReached,
    lastFailureCause,
  };
}

/**
 * Réunit les intervalles qui se chevauchent ou se touchent. Les intervalles
 * enregistrés doivent être deux à deux disjoints (invariants de EF-RBK-005).
 */
export function mergeIntervals<T extends ProgressionInterval>(intervals: readonly T[]): readonly T[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.fromProgressionMeters - b.fromProgressionMeters);
  const merged: T[] = [];

  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last !== undefined && interval.fromProgressionMeters <= last.toProgressionMeters) {
      if (interval.toProgressionMeters > last.toProgressionMeters) {
        merged[merged.length - 1] = { ...last, toProgressionMeters: interval.toProgressionMeters };
      }
      continue;
    }
    merged.push({ ...interval });
  }
  return merged;
}

/** Retire des intervalles manquants tout ce qui a finalement été couvert. */
export function subtractIntervals(
  from: readonly ProgressionInterval[],
  removed: readonly ProgressionInterval[],
): readonly ProgressionInterval[] {
  let current = from.map((interval) => ({ ...interval }));

  for (const cut of removed) {
    const next: ProgressionInterval[] = [];
    for (const interval of current) {
      if (cut.toProgressionMeters <= interval.fromProgressionMeters) {
        next.push(interval);
        continue;
      }
      if (cut.fromProgressionMeters >= interval.toProgressionMeters) {
        next.push(interval);
        continue;
      }
      if (cut.fromProgressionMeters > interval.fromProgressionMeters) {
        next.push({
          fromProgressionMeters: interval.fromProgressionMeters,
          toProgressionMeters: cut.fromProgressionMeters,
        });
      }
      if (cut.toProgressionMeters < interval.toProgressionMeters) {
        next.push({
          fromProgressionMeters: cut.toProgressionMeters,
          toProgressionMeters: interval.toProgressionMeters,
        });
      }
    }
    current = next;
  }

  return current.filter((interval) => interval.toProgressionMeters > interval.fromProgressionMeters);
}
